import httpClient from './httpClient';

const API_PREFIX = '/platform';

/**
 * Platform Admin Service
 * API calls for platform administration
 */
const platformService = {
  // ========================
  // Authentication
  // ========================

  async login(email, password) {
    const response = await httpClient.post(`${API_PREFIX}/login`, { email, password });
    return response.data;
  },

  async logout(refreshToken) {
    const response = await httpClient.post(`${API_PREFIX}/logout`, { refreshToken });
    return response.data;
  },

  async refreshTokens(refreshToken) {
    const response = await httpClient.post(`${API_PREFIX}/refresh`, { refreshToken });
    return response.data;
  },

  async getMe() {
    const response = await httpClient.get(`${API_PREFIX}/me`);
    return response.data;
  },

  // ========================
  // Platform Statistics
  // ========================

  async getStats() {
    const response = await httpClient.get(`${API_PREFIX}/stats`);
    return response.data;
  },

  async getAuditLogs(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await httpClient.get(`${API_PREFIX}/audit?${queryString}`);
    return response.data;
  },

  // ========================
  // Tenants (Companies)
  // ========================

  async getTenants(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await httpClient.get(`${API_PREFIX}/tenants?${queryString}`);
    return response.data;
  },

  async getTenant(id) {
    const response = await httpClient.get(`${API_PREFIX}/tenants/${id}`);
    return response.data;
  },

  async createTenant(data) {
    const response = await httpClient.post(`${API_PREFIX}/tenants`, data);
    return response.data;
  },

  async updateTenant(id, data) {
    const response = await httpClient.patch(`${API_PREFIX}/tenants/${id}`, data);
    return response.data;
  },

  async suspendTenant(id, reason) {
    const response = await httpClient.post(`${API_PREFIX}/tenants/${id}/suspend`, { reason });
    return response.data;
  },

  async reactivateTenant(id) {
    const response = await httpClient.post(`${API_PREFIX}/tenants/${id}/reactivate`);
    return response.data;
  },

  // ========================
  // Tenant Verification
  // ========================

  async getPendingTenants(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await httpClient.get(`${API_PREFIX}/tenants/pending?${queryString}`);
    return response.data;
  },

  async verifyTenant(id) {
    const response = await httpClient.post(`${API_PREFIX}/tenants/${id}/verify`);
    return response.data;
  },

  async rejectTenant(id, reason) {
    const response = await httpClient.post(`${API_PREFIX}/tenants/${id}/reject`, { reason });
    return response.data;
  },

  // ========================
  // Tenant Users
  // ========================

  async getTenantUsers(tenantId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await httpClient.get(`${API_PREFIX}/tenants/${tenantId}/users?${queryString}`);
    return response.data;
  },

  async createTenantUser(tenantId, data) {
    const response = await httpClient.post(`${API_PREFIX}/tenants/${tenantId}/users`, data);
    return response.data;
  },

  async updateTenantUser(tenantId, userId, data) {
    const response = await httpClient.patch(`${API_PREFIX}/tenants/${tenantId}/users/${userId}`, data);
    return response.data;
  },

  async deleteTenantUser(tenantId, userId) {
    const response = await httpClient.delete(`${API_PREFIX}/tenants/${tenantId}/users/${userId}`);
    return response.data;
  },

  // ========================
  // Tenant Modules (feature gating)
  // ========================

  async getTenantModules(tenantId) {
    const response = await httpClient.get(`${API_PREFIX}/tenants/${tenantId}/modules`);
    return response.data;
  },

  async setTenantModule(tenantId, moduleKey, enabled, settings) {
    const response = await httpClient.put(
      `${API_PREFIX}/tenants/${tenantId}/modules/${moduleKey}`,
      { enabled, settings }
    );
    return response.data;
  },

  // ========================
  // Dealer Management
  // ========================

  async getDealers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await httpClient.get(`${API_PREFIX}/dealers?${queryString}`);
    return response.data;
  },

  async getDealer(id) {
    const response = await httpClient.get(`${API_PREFIX}/dealers/${id}`);
    return response.data;
  },

  async createDealer(data) {
    const response = await httpClient.post(`${API_PREFIX}/dealers`, data);
    return response.data;
  },

  async updateDealer(id, data) {
    const response = await httpClient.patch(`${API_PREFIX}/dealers/${id}`, data);
    return response.data;
  }
};

export default platformService;
